/**
 * Admin → Legal → KYC
 * Dashboard de estados de verificación KYC por tenant (LEGAL-GAP-08).
 * Datos mínimos: status, provider, fecha — NUNCA documentos/biometría.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LegalBackLink } from "@/components/legal/legal-admin-shared";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/ops/legal/kyc")({
  component: LegalKycPage,
  head: () => ({ meta: [{ title: "KYC — Admin" }] }),
});

type KycStatus = "pending" | "in_progress" | "approved" | "rejected" | "expired";

interface KycRecordRow {
  id: string;
  tenantId: string;
  tenantName: string | null;
  tenantSlug: string | null;
  provider: string;
  providerSessionId: string | null;
  status: KycStatus;
  rejectionCode: string | null;
  approvedAt: string | null;
  updatedAt: string;
  createdAt: string;
}

const KYC_STATUS_LABELS: Record<KycStatus, { label: string; color: string }> = {
  pending:     { label: "Pendiente",   color: "text-amber-600" },
  in_progress: { label: "En curso",    color: "text-blue-600" },
  approved:    { label: "Aprobado",    color: "text-green-600" },
  rejected:    { label: "Rechazado",   color: "text-red-600" },
  expired:     { label: "Expirado",    color: "text-muted-foreground" },
};

const ALL_STATUSES: KycStatus[] = ["pending", "in_progress", "approved", "rejected", "expired"];

function OverrideRow({ record, onDone }: { record: KycRecordRow; onDone: () => void }) {
  const [busy, setBusy] = useState(false);

  const override = async (status: KycStatus) => {
    setBusy(true);
    try {
      const r = await fetch("/api/admin/kyc", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tenantId: record.tenantId, status }),
      });
      if (!r.ok) {
        const err = (await r.json().catch(() => ({}))) as { error?: string };
        toast.error(err.error ?? "No se pudo aplicar el override");
        return;
      }
      toast.success("Override aplicado");
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs text-green-700"
        disabled={busy || record.status === "approved"}
        onClick={() => void override("approved")}
      >
        Aprobar
      </Button>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs text-red-700"
        disabled={busy || record.status === "rejected"}
        onClick={() => void override("rejected")}
      >
        Rechazar
      </Button>
    </div>
  );
}

function LegalKycPage() {
  const [records, setRecords] = useState<KycRecordRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<KycStatus | "all">("all");
  const [tenantFilter, setTenantFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const pageSize = 20;

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (statusFilter !== "all") params.set("status", statusFilter);
    if (tenantFilter.trim()) params.set("tenantId", tenantFilter.trim());
    fetch(`/api/admin/kyc?${params.toString()}`)
      .then((r) => r.json())
      .then((d: { records?: KycRecordRow[]; total?: number }) => {
        setRecords(d.records ?? []);
        setTotal(d.total ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(reload, [page, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-6">
      <LegalBackLink />
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Verificación KYC</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Estados de verificación comercial por tenant (Didit). Política de datos mínimos activa.
        </p>
      </div>

      <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
        <p className="text-sm font-medium text-green-800 dark:text-green-200">
          Datos mínimos KYC — qué se almacena
        </p>
        <ul className="mt-2 space-y-1 text-xs text-green-700 dark:text-green-300">
          <li>✅ Status (pending / in_progress / approved / rejected / expired)</li>
          <li>✅ Provider (didit, u otro) y session_id (solo para idempotencia)</li>
          <li>✅ Código de rechazo (si corresponde) y fecha de aprobación</li>
          <li>❌ NUNCA: DNI, documentos, fotos, selfies, biometría, video</li>
        </ul>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="text-base">Registros ({total})</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <input
              placeholder="Filtrar por tenantId…"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  reload();
                }
              }}
              className="h-8 w-48 rounded-md border border-border bg-background px-2 text-xs"
            />
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v as KycStatus | "all");
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {KYC_STATUS_LABELS[s].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading && <p className="px-4 py-3 text-sm text-muted-foreground">Cargando…</p>}
          {!loading && records.length === 0 && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Sin registros KYC para este filtro.
            </p>
          )}
          {!loading &&
            records.map((r) => {
              const meta = KYC_STATUS_LABELS[r.status];
              return (
                <div
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3"
                >
                  <div className="min-w-0 space-y-0.5">
                    <p className="font-medium text-foreground">
                      {r.tenantName ?? r.tenantId}
                      {r.tenantSlug && (
                        <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                          /{r.tenantSlug}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {r.provider} · actualizado {new Date(r.updatedAt).toLocaleString("es-AR")}
                      {r.rejectionCode && ` · motivo: ${r.rejectionCode}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={meta.color}>
                      {meta.label}
                    </Badge>
                    <OverrideRow record={r} onDone={reload} />
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="text-xs text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}
